import { AlertTriangle, ExternalLink, Link2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Location, Product } from "../../app/types.js";
import { displayErrorMessage, patchJson, postJson } from "../../lib/api.js";
import { normalizeFoodName } from "@household/shared/mealAssembler";

// Reusable create/edit dialog for a Food Catalog product (#642). Replaces the
// bare inline create form (no duplicate detection, no post-create edit path
// for name/category/unit/section/place) with one dialog that both create and
// edit flows share. Props are shaped so the shopping list-builder's "Create
// new…" picker (#635) can reuse this without change: pass `initialName` to
// prefill from typed text, `product` (with mode: "edit") to prefill from an
// existing row, and read the saved/updated product back via `onSaved`.

export type ProductDefinitionDialogMode = "create" | "edit";

export type ProductDefinitionDialogProps = {
  open: boolean;
  mode: ProductDefinitionDialogMode;
  /** Prefills the name field in create mode (e.g. text typed into a picker). */
  initialName?: string;
  /** Prefills the usual-unit field in create mode (e.g. the unit a shopper
   * actually bought it in). Falls back to "each" like the bare create default. */
  initialUnit?: string;
  /** The product being edited. Required (and used to prefill every field) in edit mode. */
  product?: Product;
  locations: Location[];
  /** Existing products, used for the fuzzy duplicate check on create. */
  products: Product[];
  onSaved: (product: Product) => void;
  onClose: () => void;
  /** Optional: follow the cross-link to the Thing template (#1043). Omitted
   * where there is nowhere to navigate to, in which case the line still names
   * the template — the fact is worth stating even without a jump. */
  onOpenTemplate?: () => void;
  /** Optional: leave the dialog for the food's own page (#1045). Omitted by the
   * page itself, which is already there, and by create-mode callers, where
   * there is no food to open yet. */
  onOpenFullPage?: (product: Product) => void;
  /** Optional: called when the user follows the duplicate warning's action,
   * with the existing product they matched. The dialog does NOT close itself
   * here — the caller owns what happens next (open it for editing, link the
   * row it came from, …) and closes or replaces the dialog accordingly.
   * When omitted the action is not rendered at all (#698): a button that only
   * dismissed the dialog left the user's row unlinked with no way forward. */
  onJumpToEdit?: (product: Product) => void;
  /** Wording for that action. "edit" (the Food Catalog default) offers to open
   * the existing definition; "link" is for callers whose row/cart item just
   * needs to point at it, where "jump to edit" would misdescribe the result. */
  duplicateAction?: "edit" | "link";
};

type FormState = {
  name: string;
  category: string;
  defaultUnit: string;
  storeSection: string;
  shelfLife: string;
  defaultLocationId: string;
  notes: string;
};

function stateFromProduct(product: Product | undefined, initialName: string, initialUnit: string): FormState {
  return {
    name: product?.name ?? initialName,
    category: product?.category ?? "",
    defaultUnit: product?.defaultUnit ?? initialUnit ?? "each",
    storeSection: product?.storeSection ?? "",
    shelfLife: product?.defaultShelfLifeDays ? String(product.defaultShelfLifeDays) : "",
    defaultLocationId: product?.defaultLocationId ?? "",
    notes: product?.notes ?? ""
  };
}

/** Finds an existing product (other than the one being edited) whose
 * normalized name matches the normalized draft name. Exported so callers that
 * want a lighter-weight inline check (e.g. Quick Add) can reuse it without
 * mounting the whole dialog. */
export function findDuplicateProduct(name: string, products: Product[], excludeId?: string): Product | undefined {
  const normalized = normalizeFoodName(name);
  if (!normalized) return undefined;
  return products.find((product) => {
    if (excludeId && String(product.id) === String(excludeId)) return false;
    return normalizeFoodName(product.name) === normalized;
  });
}

export function ProductDefinitionDialog({
  open,
  mode,
  initialName = "",
  initialUnit = "",
  product,
  locations,
  products,
  onSaved,
  onClose,
  onOpenTemplate,
  onOpenFullPage,
  onJumpToEdit,
  duplicateAction = "edit"
}: ProductDefinitionDialogProps) {
  const { t, ready } = useTranslation("groceries");
  const [form, setForm] = useState<FormState>(() => stateFromProduct(product, initialName, initialUnit));
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(stateFromProduct(product, initialName, initialUnit));
    setConfirmDuplicate(false);
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, product?.id]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    if (open) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const duplicate = useMemo(
    () => (mode === "create" ? findDuplicateProduct(form.name, products) : undefined),
    [mode, form.name, products]
  );

  // Closed comes first: a dialog nobody opened renders no translated text at
  // all, so it should render nothing, not a loading box floating behind the
  // page it is not covering.
  if (!open) return null;

  // Then its own namespace gate, because the gate above it is testing a
  // different namespace. That is the general rule: a component that reads a
  // lazy namespace and can mount outside that namespace's gated root has to
  // gate itself. This dialog opens from ShoppingView (gated on "shopping")
  // and, more to the point, from ListSheet, which App mounts as a sibling of
  // the whole tab shell — so it can open from ANY tab with no groceries-gated
  // ancestor anywhere above it. It escapes the tab model entirely.
  //
  // Below every hook (an early return above one makes React throw when `ready`
  // flips) and above every t() in the JSX below.
  if (!ready) return <div className="panel-loading" aria-busy="true" />;

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    if (key === "name") setConfirmDuplicate(false);
  }

  async function save() {
    if (!form.name.trim()) {
      setError(t("error.nameRequired"));
      return;
    }
    if (duplicate && !confirmDuplicate) {
      // First click on a likely duplicate only surfaces the warning; the
      // caller must confirm explicitly before the request goes out.
      setConfirmDuplicate(true);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const shelfLife = Number(form.shelfLife);
      const payload = {
        name: form.name.trim(),
        category: form.category.trim(),
        defaultUnit: form.defaultUnit.trim() || "each",
        storeSection: form.storeSection.trim(),
        notes: form.notes.trim(),
        defaultShelfLifeDays: shelfLife > 0 ? Math.round(shelfLife) : null,
        defaultLocationId: form.defaultLocationId || null
      };
      const saved =
        mode === "edit" && product
          ? await patchJson<Product>(`/api/v1/products/${product.id}`, payload)
          : await postJson<Product>("/api/v1/products", payload);
      onSaved(saved);
      onClose();
    } catch (err) {
      setError(displayErrorMessage(err, mode === "edit" ? t("error.couldNotSaveChanges") : t("error.couldNotCreateFood")));
    } finally {
      setBusy(false);
    }
  }

  const title = mode === "edit"
    ? t("dialog.editFood", { name: product?.name ?? t("dialog.foodFallback") })
    : t("dialog.newFood");

  return (
    <div
      className="attachment-viewer product-definition-modal"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div className="attachment-viewer-panel" onClick={(event) => event.stopPropagation()}>
        <div className="attachment-viewer-header">
          <div className="attachment-viewer-title">
            <strong>{title}</strong>
          </div>
          <div className="attachment-viewer-header-actions">
            <button className="icon-button" type="button" onClick={onClose} aria-label={t("dialog.close")}>
              <X size={17} />
            </button>
          </div>
        </div>
        <form
          className="product-definition-form"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <label>
            {t("field.food")}
            <input
              value={form.name}
              onChange={(event) => update("name", event.currentTarget.value)}
              placeholder={t("field.foodPlaceholder")}
              required
              autoFocus
            />
          </label>
          <label>
            {t("field.category")}
            <input value={form.category} onChange={(event) => update("category", event.currentTarget.value)} placeholder={t("field.categoryPlaceholder")} />
          </label>
          <label>
            {t("field.usualUnit")}
            {/* The hint mirrors the literal `|| "each"` this form falls back to
                below, so it stays English in every language: a translated hint
                would seed a second unit vocabulary the stock plans cannot
                compare against the first. */}
            <input value={form.defaultUnit} onChange={(event) => update("defaultUnit", event.currentTarget.value)} placeholder={t("field.usualUnitPlaceholder")} />
          </label>
          <label>
            {t("field.storeSection")}
            <input value={form.storeSection} onChange={(event) => update("storeSection", event.currentTarget.value)} placeholder={t("field.storeSectionPlaceholder")} />
          </label>
          <label>
            {t("field.shelfLife")}
            <input
              type="number"
              min="1"
              step="1"
              value={form.shelfLife}
              onChange={(event) => update("shelfLife", event.currentTarget.value)}
              placeholder={t("field.shelfLifePlaceholder")}
            />
          </label>
          <label>
            {t("field.usualPlace")}
            <select value={form.defaultLocationId} onChange={(event) => update("defaultLocationId", event.currentTarget.value)}>
              <option value="">{t("field.noUsualPlace")}</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>
          <label className="product-definition-notes">
            {t("field.notes")}
            <textarea
              value={form.notes}
              onChange={(event) => update("notes", event.currentTarget.value)}
              placeholder={t("field.notesPlaceholder")}
              rows={2}
            />
          </label>
          {duplicate ? (
            <p className="inline-note product-definition-duplicate-warning" role="alert">
              <AlertTriangle size={14} />{" "}
              {onJumpToEdit
                ? (duplicateAction === "link"
                  ? t("duplicate.linkPrompt", { name: duplicate.name })
                  : t("duplicate.editPrompt", { name: duplicate.name }))
                : t("duplicate.plain", { name: duplicate.name })}
              {onJumpToEdit ? " " : null}
              {onJumpToEdit ? (
                <button type="button" className="link-button" onClick={() => onJumpToEdit(duplicate)}>
                  {duplicateAction === "link"
                    ? t("duplicate.use", { name: duplicate.name })
                    : t("duplicate.jump", { name: duplicate.name })}
                </button>
              ) : null}
            </p>
          ) : null}
          {/* The reverse side of the Food/Things cross-link (#1043). Read-only
              here on purpose: the definitions screen is the single writer, so
              there is exactly one place where a link is made or broken, and no
              way for two screens to disagree about it. Deliberately not a
              Manage-foods grid column either — grid columns are for
              bulk-correctable fields (docs/food-catalog-grid.md). */}
          {mode === "edit" && product?.assetDefinition ? (
            <p className="inline-note product-definition-link" data-asset-definition-id={product.assetDefinition.id}>
              <Link2 size={13} aria-hidden="true" /> {t("link.thingTemplate")} <strong>{product.assetDefinition.name}</strong>
              {t("link.notSynced")}
              {/* Only when a caller actually wired the jump (codex review). A
                  button that closes the dialog and goes nowhere is worse than
                  no button: the fact itself is still worth stating. */}
              {onOpenTemplate ? (
                <>
                  {" "}
                  <button type="button" className="link-button" onClick={() => { onClose(); onOpenTemplate(); }}>
                    <ExternalLink size={12} /> {t("link.openInThings")}
                  </button>
                </>
              ) : null}
            </p>
          ) : null}
          {error ? <p className="inline-note">{error}</p> : null}
          <div className="product-definition-actions">
            {/* The way through to everything this dialog deliberately does not
                hold — lots, chores, photos, comments (#1045). Same discipline as
                the Things jump above: rendered only where a caller wired it. */}
            {mode === "edit" && product && onOpenFullPage ? (
              <button type="button" onClick={() => { onClose(); onOpenFullPage(product); }} disabled={busy}>
                <ExternalLink size={14} /> {t("action.openFullPage")}
              </button>
            ) : null}
            <button type="button" onClick={onClose} disabled={busy}>
              {t("action.cancel")}
            </button>
            <button type="submit" className="primary" disabled={busy}>
              {duplicate && !confirmDuplicate
                ? t("action.saveAnyway")
                : mode === "edit" ? t("action.saveChanges") : t("action.addFood")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
